import React, { Fragment, useState } from 'react';
import { useClipboard } from '@/hooks';
import TrackingService from '@/services/tracking';
import { theme } from '@/styles';
import { ShopDetailViewInfo } from '@/types';
import Tooltip from '@/ui/Tooltip/Tooltip';
import styled from 'styled-components';
import {
  BasicPopup,
  BookmarkButton,
  Box,
  HStack,
  Icongraphy,
  NewContentButton,
  NewFlag,
  Text,
  VStack,
} from '@design-system/pc';
import {
  SHOP_DETAIL_VIEW_TYPE_DESIGNPARK,
  SHOP_DETAIL_VIEW_TYPE_INTERIOR,
  SHOP_DETAIL_VIEW_TYPE_RHKB,
  SHOP_DETAIL_VIEW_TYPE_SHOWROOM,
} from '@/constants';
// 상담 분야 카드 데이터 타입
interface ConsultFieldData {
  icon: string;
  title: string;
  subtitle: string;
  flags: string[];
  flagColor: string;
  consultType: 'HF' | 'RM';
}
interface ConsultFieldCardProps {
  data: ConsultFieldData;
  handleConsult: (consultType: 'HF' | 'RM') => void;
}
interface FloatingInfoProps {
  data: ShopDetailViewInfo;
  handleScrap: () => void;
  isScrap: boolean;
  handleConsult: (consultType: 'HF' | 'RM') => void;
  isOpenConsultPopup: boolean;
  handleOpenConsultPopup: () => void;
  handleCloseConsultPopup: () => void;
}

const infoList = [
  { code: 'tell', name: '전화번호' },
  { code: 'address', name: '매장주소' },
  { code: 'items', name: '취급항목' },
  { code: 'operatingTime', name: '운영시간' },
  { code: 'floorInfo', name: '층별정보' },
];

const consultFields: ConsultFieldData[] = [
  {
    consultType: 'HF',
    icon: 'ic_sofa',
    title: '가구, 패브릭',
    subtitle: '직접 체험하고, 스타일링 제안 받기',
    flags: ['가구 체험', '홈퍼니싱 전문가', '스타일링 제안'],
    flagColor: 'color_chip.red.50',
  },
  {
    consultType: 'RM',
    icon: 'ic_remodeling_start',
    title: '전체, 부분 공사',
    subtitle: '우리 집 도면으로 1:1 상담받기',
    flags: ['우리집 맞춤', '인테리어 전문가', '무료 견적'],
    flagColor: 'color_chip.blue.50',
  },
];

const FloatingInfo = ({
  data,
  isScrap,
  handleScrap,
  handleConsult,
  isOpenConsultPopup,
  handleOpenConsultPopup,
  handleCloseConsultPopup,
}: FloatingInfoProps) => {
  const { handleCopy } = useClipboard();
  const [copyState, setCopyState] = useState<boolean>(false);
  const { header, bottomBar, viewType } = data;

  return (
    <FloatingContainer {...TrackingService.tagGTM('shop_info')}>
      <StoreKindContainer>
        <StoreKind variant="body2" color="color_chip.gray.700">
          {header?.type.name}
        </StoreKind>
        {header.type.code !== 'S04' && (
          <Tooltip
            title={header?.tooltipTitle}
            text={header?.tooltipDescription}
            position="left"
            gtmName="shop_info_type_tooltip"
          >
            <IconBox>
              <Icongraphy variant="ic_question" size={16} resize />
            </IconBox>
          </Tooltip>
        )}
      </StoreKindContainer>
      <StoreName variant="subTitle2" color="color_chip.primary">
        {header?.name}
      </StoreName>
      <ButtonContainer>
        <BookmarkIcon
          onClick={handleScrap}
          {...TrackingService.tagGTM('shop_info_bookmark_button')}
        >
          <BookmarkButton variant="whiteBG" size={'lg'} isChecked={isScrap} />
        </BookmarkIcon>
        {viewType === SHOP_DETAIL_VIEW_TYPE_DESIGNPARK && (
          <NewContentButton
            size="lg"
            emphasis="high"
            width="100%"
            disabled={!bottomBar.reserveVisitYn}
            onClick={handleOpenConsultPopup}
            style={{
              cursor: !bottomBar.reserveVisitYn ? 'default' : 'pointer',
            }}
            {...TrackingService.tagGTM('shop_consult_select_popup_open_button')}
          >
            매장 상담 신청
          </NewContentButton>
        )}
        {viewType === SHOP_DETAIL_VIEW_TYPE_SHOWROOM && (
          <NewContentButton
            size="lg"
            emphasis="high"
            width="100%"
            disabled={!bottomBar.reserveVisitYn}
            onClick={() => {
              handleConsult('RM');
            }}
            style={{
              cursor: !bottomBar.reserveVisitYn ? 'default' : 'pointer',
            }}
            {...TrackingService.tagGTM('shop_info_sequential_consult_button')}
          >
            매장 상담 신청
          </NewContentButton>
        )}
        {(viewType === SHOP_DETAIL_VIEW_TYPE_RHKB ||
          viewType === SHOP_DETAIL_VIEW_TYPE_INTERIOR) && (
          <NewContentButton
            size="lg"
            emphasis="high"
            width="100%"
            disabled={!bottomBar.reserveVisitYn}
            onClick={() => {
              handleConsult('HF');
            }}
            style={{
              cursor: !bottomBar.reserveVisitYn ? 'default' : 'pointer',
            }}
            {...TrackingService.tagGTM('shop_info_visit_consult_button')}
          >
            매장 상담 신청
          </NewContentButton>
        )}
      </ButtonContainer>
      <InfoContainer>
        {infoList.map((item, index) => {
          return (
            <Fragment key={`info_list_fragment_${index}`}>
              {item.code === 'tell' && header?.telNumber.telYn === 'Y' && (
                <InfoListItem className="InfoList">
                  <InfoTitle variant="body2" color="color_chip.gray.600">
                    {item.name}
                  </InfoTitle>
                  <InfoContents>
                    <Content>{header?.telNumber.number}</Content>
                  </InfoContents>
                </InfoListItem>
              )}
              {item.code === 'address' && (
                <InfoListItem className="InfoList">
                  <InfoTitle variant="body2" color="color_chip.gray.600">
                    {item.name}
                  </InfoTitle>
                  <InfoContents>
                    <Content>
                      {header?.address.basic} {header?.address.detail}
                    </Content>
                  </InfoContents>
                  <IconBox
                    onClick={() => {
                      handleCopy(
                        `${header?.address.basic} ${header?.address.detail}`,
                      );
                      setCopyState(true);
                    }}
                    {...TrackingService.tagGTM('shop_info_address_copy_button')}
                  >
                    <Icongraphy variant="ic_copy" size={20} resize />
                    {copyState && (
                      <CopyTooltip
                        backgroundColor="color_chip.white"
                        border="1px solid"
                        borderColor="color_chip.gray.500"
                      >
                        <CopyTooltipText
                          variant="body2"
                          color="color_chip.primary"
                        >
                          {`매장 주소가 클립보드에\n복사되었습니다.`}
                        </CopyTooltipText>
                        <CopyTooltipIcon
                          onClick={(e) => {
                            e.stopPropagation();
                            setCopyState(false);
                          }}
                          {...TrackingService.tagGTM(
                            'shop_info_address_copy_tooltip_close_button',
                          )}
                        >
                          <Icongraphy variant="ic_close" size={16} resize />
                        </CopyTooltipIcon>
                      </CopyTooltip>
                    )}
                  </IconBox>
                </InfoListItem>
              )}

              {item.code === 'items' && (
                <InfoListItem className="InfoList">
                  <InfoTitle variant="body2" color="color_chip.gray.600">
                    {item.name}
                  </InfoTitle>
                  <InfoContents>
                    <Content>{header?.includeItem}</Content>
                  </InfoContents>
                </InfoListItem>
              )}

              {item.code === 'operatingTime' && (
                <InfoListItem className="InfoList">
                  <InfoTitle variant="body2" color="color_chip.gray.600">
                    {item.name}
                  </InfoTitle>
                  <InfoContents>
                    <Content>
                      {header?.operateTime.weekday.day}{' '}
                      {header?.operateTime.weekday.time}
                    </Content>
                    <Content>
                      {' '}
                      {header?.operateTime.weekend.day}{' '}
                      {header?.operateTime.weekend.time}
                    </Content>
                    <Content>
                      {' '}
                      {header?.operateTime.closed.day}{' '}
                      {header?.operateTime.closed.time}
                    </Content>
                  </InfoContents>
                </InfoListItem>
              )}

              {item.code === 'floorInfo' && header?.floorGuides.length !== 0 && (
                <InfoListItem className="InfoList">
                  <InfoTitle variant="body2" color="color_chip.gray.600">
                    {item.name}
                  </InfoTitle>
                  <InfoContents>
                    {header?.floorGuides.map((_, idx) => (
                      <Content key={`${idx}_floorInfo`}>
                        {_.floor} {_.description}
                      </Content>
                    ))}
                    <Content>{header?.amenities}</Content>
                  </InfoContents>
                </InfoListItem>
              )}
            </Fragment>
          );
        })}
      </InfoContainer>
      <BasicPopup
        handleClose={handleCloseConsultPopup}
        size="md"
        title="상담 분야"
        isOpen={isOpenConsultPopup}
      >
        <VStack space={16} my={12}>
          {consultFields.map((field) => (
            <ConsultFieldCard
              key={`select-consult-field-${field.consultType}`}
              data={field}
              handleConsult={handleConsult}
            />
          ))}
        </VStack>
      </BasicPopup>
    </FloatingContainer>
  );
};

export default FloatingInfo;

const ConsultFieldCard: React.FC<ConsultFieldCardProps> = ({
  data,
  handleConsult,
}) => (
  <VStack
    width="100%"
    onClick={() => handleConsult(data.consultType)}
    {...TrackingService.tagGTM(
      data.consultType === 'HF'
        ? 'shop_info_visit_consult_button'
        : 'shop_info_sequential_consult_button',
    )}
    style={{
      cursor: 'pointer',
    }}
  >
    <HStack
      width="100%"
      padding="20px 16px 20px 20px"
      space="space-between"
      border="1px solid"
      borderColor="color_chip.gray.200"
    >
      <VStack height="100%" width="100%" space={16} alignment="start">
        <HStack height="100%" space={24}>
          <HStack
            width={94}
            height={94}
            backgroundColor="color_chip.gray.50"
            justifyContent="center"
          >
            <Icongraphy
              variant={data.icon as any}
              width={48}
              height={48}
              resize
            />
          </HStack>
          <VStack alignment="start" space={16}>
            <VStack alignment="start" space={2} width="100%">
              <Text variant="headline3" color="color_chip.primary">
                {data.title}
              </Text>
              <Text variant="body1" color="color_chip.gray.700">
                {data.subtitle}
              </Text>
            </VStack>
            <HStack space={8}>
              {data.flags.map((flag, index) => (
                <NewFlag
                  key={`bottom-sheet-${data.consultType}-flag-${index}`}
                  variant="solid"
                  size="sm"
                  content={flag}
                  color={data.flagColor}
                  textColor="color_chip.primary"
                />
              ))}
            </HStack>
          </VStack>
        </HStack>
      </VStack>
      <Icongraphy variant="ic_arrow_right_bold" width={24} height={24} resize />
    </HStack>
  </VStack>
);

const FloatingContainer = styled.div`
  min-width: 352px;
  width: 352px;
  margin-top: 40px;
  position: sticky;
  top: 184px;
  height: fit-content;
  bottom: 0px;
`;
const StoreKindContainer = styled.div`
  display: flex;
  gap: 2px;
  align-items: center;
`;

const StoreKind = styled(Text)``;

const IconBox = styled(Box)`
  position: relative;
  cursor: pointer;
`;

const StoreName = styled(Text)`
  display: block;
  margin-bottom: 20px;
`;

const ButtonContainer = styled(HStack)`
  align-items: center;
  width: 352px;
  padding-right: 10px;
  gap: 8px;
  margin-bottom: 36px;
`;

const BookmarkIcon = styled.div`
  min-width: 48px;
  min-height: 48px;
  cursor: pointer;
`;

const InfoContainer = styled.div`
  width: 100%;
  padding: 24px 28px 24px 36px;
  max-height: 537px;
  overflow: auto;
  border-radius: 2px;
  background-color: ${theme.colors.accent_colors.primary.light};
`;

const InfoTitle = styled(Text)`
  min-width: 60px;
`;

const InfoListItem = styled.div`
  display: flex;
  padding-top: 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid ${theme.colors.neutral_colors.gray[200]};
  gap: 10px;
  :first-child {
    padding-top: 0px;
  }
  :last-child {
    padding-bottom: 0px;
    border: none;
  }
`;

const InfoContents = styled(VStack)`
  align-items: flex-start;
  gap: 4px;
`;

const Content = styled.div`
  width: 100%;
  display: flex;
  gap: 8px;
  word-break: keep-all;
  text-overflow: ellipsis;
  color: ${theme.colors.accent_colors.primary.default};
  ${theme.typography.body2};
`;

// const ContentTwo = styled.div``;

const CopyTooltip = styled(Box)`
  position: absolute;
  display: flex;
  width: 232px;
  height: 72px;
  padding: 16px 12px 16px 16px;
  gap: 4px;
  right: 0px;
  justify-content: space-between;
  border-radius: 4px;
`;

const CopyTooltipText = styled(Text)`
  white-space: pre-wrap;
`;

const CopyTooltipIcon = styled.div`
  width: 20px;
  height: 20px;
  cursor: pointer;
`;
